<template>
  <div class="flex flex-col items-start gap-2 self-stretch">
    <div class="flex items-center justify-between gap-2">
      <UITextXsMedium v-if="!props.isElementLevel">
        {{ t("common.style.labelAlignment.labelAlignment") }}
      </UITextXsMedium>
      <span v-else class="text-sm font-medium text-gray-700">
        {{ t("common.style.labelAlignment.labelAlignment") }}
      </span>
      <UIDropdown id="desktop-mobile-toggle" :options="deviceOptions" @select="switchDeviceMode">
        <template #default>
          <div class="flex items-center gap-0.5">
            <Phone01Icon v-if="selectedDeviceMode === 'mobile'" class="h-4 w-4 cursor-pointer" />
            <Monitor04Icon v-else class="h-4 w-4 cursor-pointer" />
            <ChevronDownIcon class="h-3 w-3" />
          </div>
        </template>
      </UIDropdown>
    </div>
    <div class="flex w-full gap-2">
      <div
        v-for="alignmentOption in LABEL_ALIGNMENT.filter(
          (item) => item.key !== 'default' || props.isElementLevel
        )"
        :key="alignmentOption.key"
        class="label-box"
        :class="[
          effectiveAlignment === alignmentOption.key ? 'active' : '',
          isMobileMode && alignmentOption.key !== 'top' ? 'disabled' : '',
        ]"
        @click="handleLabelAlignment(alignmentOption.key as AlignmentPosition)"
      >
        <UITooltip v-if="alignmentOption.icon" trigger="hover" placement="top">
          <template #trigger>
            <component
              :is="alignmentOption.icon"
              class="focus_outline_none"
              :class="effectiveAlignment === alignmentOption.key ? 'active-svg' : ''"
            ></component>
          </template>
          {{ alignmentOption.name }}
        </UITooltip>
        <span v-else class="focus_outline_none w-full whitespace-nowrap text-center">
          {{ t("common.formDefault") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { LABEL_ALIGNMENT, deviceOptions } from "@/util/constants";
import { AlignmentPosition } from "@/util/methods";
import { ChevronDownIcon, Monitor04Icon, Phone01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIDropdown, UITextXsMedium, UITooltip } from "@gohighlevel/ghl-ui";
import { PropType, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();
const store = useAppStore();

const handleLabelAlignment = (labelType: AlignmentPosition) => {
  if (isMobileMode.value) {
    return;
  }
  emit("update:labelAlignment", labelType);
};

const props = defineProps({
  isElementLevel: {
    type: Boolean,
    default: false,
  },
  labelAlignment: {
    type: String as PropType<AlignmentPosition>,
    required: true,
    default: (props) => {
      return props.isElementLevel ? AlignmentPosition.DEFAULT : AlignmentPosition.TOP;
    },
  },
});

const emit = defineEmits(["update:labelAlignment"]);
const selectedDeviceMode = ref<string>(store.deviceMode);

const switchDeviceMode = (value: string) => {
  selectedDeviceMode.value = value;
};

const isMobileMode = computed(() => selectedDeviceMode.value === "mobile");

const effectiveAlignment = computed<AlignmentPosition>(() =>
  isMobileMode.value ? AlignmentPosition.TOP : (props.labelAlignment as AlignmentPosition)
);
</script>

<style scoped>
.label-box {
  display: flex;
  padding: 9px 12px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}

.active {
  border: 1px solid #155eef;
  background: #eff4ff;
}

.active-svg {
  fill: #155eef;
  stroke: #155eef;
}

.focus_outline_none:focus {
  outline: none;
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f9fafb;
  border-color: #e5e7eb;
}
</style>
